import { PageContainer } from '../../components/layout/PageContainer.js';
import { ImpersonationBanner } from '../../components/ui/ImpersonationBanner.js';
import { useAuth } from '../auth/useAuth.js';
import { useMachines } from '../../contexts/MachineContext.js';
import { StatRow } from './components/StatRow.js';
import { MachineGrid } from './components/MachineGrid.js';
import { OEEPanel } from './components/OEEPanel.js';
import { IntelligencePanel } from './components/IntelligencePanel.js';
import { ToolLifePanel } from './components/ToolLifePanel.js';
import { SchedulePanel } from './components/SchedulePanel.js';
import { ToolCustodyPanel } from '../custody/ToolCustodyPanel.js';
import { useDashboardData } from './hooks/useDashboardData.js';

export function DashboardPage(): JSX.Element {
  const { shopId, isOperator } = useAuth();
  // Machines come from MachineContext (role-filtered + impersonation-aware)
  const { machines, isLoading, isError, refetch } = useMachines();
  const { summary } = useDashboardData(shopId);

  return (
    <>
      <ImpersonationBanner />
      <PageContainer>
        <div className="mb-5">
          <h1 className="font-mono text-base">Floor overview</h1>
          <p className="text-xs text-text-dim">
            Real-time state across the shop. Cards refresh as machines report in.
          </p>
        </div>

        <StatRow summary={summary} machines={machines} loading={isLoading && !summary} />

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <MachineGrid machines={machines} loading={isLoading} error={isError ? new Error('failed') : undefined} onRetry={refetch} />
            </div>
            <div className="md:col-span-2">
              <OEEPanel machines={machines} loading={isLoading} />
            </div>
          </div>
          {!isOperator && (
            <div className="lg:row-span-2">
              <IntelligencePanel machines={machines} />
            </div>
          )}
        </div>

        {/* Tool panels: hidden for operators (they use the machine detail page) */}
        {!isOperator && (
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-3">
            <ToolLifePanel shopId={shopId} machineId={machines?.[0]?.machine_id ?? null} />
            <ToolCustodyPanel shopId={shopId} />
            <SchedulePanel />
          </div>
        )}
      </PageContainer>
    </>
  );
}
